import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { ISeason, ISeasonUpdateResult, ISeasonWritePayload } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { Button } from '../ui';
import SeasonFieldset, { SeasonFieldsValue } from '../forms/SeasonFieldset';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface SeasonFormProps {
  courseId: number;
  /** The season being edited — seeds the form. */
  season: ISeason;
  /** Called with the update result after a successful save (parent refetches). */
  onSaved: (result: ISeasonUpdateResult) => void;
  onCancel: () => void;
  /** Reports whether the fields still match the season (host modal guards on it). */
  onDirtyChange?: (dirty: boolean) => void;
}

/** Seed the shared season fields from a persisted season row. */
const seedFromSeason = (s: ISeason): SeasonFieldsValue => ({
  termStart: s.termStart ? s.termStart.slice(0, 10) : '',
  termEnd: s.termEnd ? s.termEnd.slice(0, 10) : '',
  capacity: s.capacity != null ? String(s.capacity) : '',
  // numeric columns come back as strings
  pricePerSession: s.pricePerSession != null ? String(s.pricePerSession) : '',
  enrollmentPolicy: s.enrollmentPolicy,
  instructorId: s.instructorId,
});

/**
 * Edits a single season's term / capacity / per-session price / enrollment
 * policy / instructor via PUT /api/courses/:id/seasons/:seasonId. Reuses the
 * shared SeasonFieldset (the same non-entity primitives the course create form
 * uses). Surfaces the 409 term-overlap error (code SEASON_TERM_OVERLAP) inline
 * and reports what a term change could not carry (`unresolved` enrollments left
 * on the old term, pending charges stranded on the old window).
 */
const SeasonForm: React.FC<SeasonFormProps> = ({ courseId, season, onSaved, onCancel, onDirtyChange }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const [fields, setFields] = useState<SeasonFieldsValue>(() => seedFromSeason(season));
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  const [saving, setSaving] = useState(false);
  const [overlapError, setOverlapError] = useState<string | null>(null);

  const baseline = useMemo(() => JSON.stringify(seedFromSeason(season)), [season]);
  useEffect(() => {
    onDirtyChangeRef.current?.(JSON.stringify(fields) !== baseline);
  }, [fields, baseline]);

  const termOrderError =
    fields.termStart !== '' && fields.termEnd !== '' && fields.termEnd < fields.termStart
      ? t('courses.renew.termOrderError')
      : undefined;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (termOrderError) return;
    setSaving(true);
    setOverlapError(null);
    try {
      const payload: ISeasonWritePayload = {
        termStart: fields.termStart === '' ? null : fields.termStart,
        termEnd: fields.termEnd === '' ? null : fields.termEnd,
        capacity: fields.capacity === '' ? null : Number(fields.capacity),
        pricePerSession: fields.pricePerSession === '' ? null : Number(fields.pricePerSession),
        enrollmentPolicy: fields.enrollmentPolicy,
        instructorId: fields.instructorId,
      };
      const { data } = await api.courses.seasons.update(courseId, season.id, payload);
      addToast(t('courses.seasons.updated'), 'success');
      // A term change that could not carry every enrollment/charge leaves money
      // on the old window. "Season updated" alone hides that.
      if (data.needsReview) {
        addToast(
          t('dataSafety.seasonRealignNeedsReview', {
            unresolved: data.unresolved ?? 0,
            stranded: data.strandedCharges ?? 0,
          }),
          'warning',
        );
      } else if ((data.realigned ?? 0) > 0) {
        addToast(
          t('dataSafety.seasonRealigned', {
            realigned: data.realigned ?? 0,
            intents: data.intentsRealigned ?? 0,
          }),
          'info',
        );
      }
      onSaved(data);
    } catch (err: any) {
      const code = err.response?.data?.code;
      if (code === 'SEASON_TERM_OVERLAP') {
        setOverlapError(t('courses.seasons.overlapError'));
      } else {
        addToast(translateError(err, t, 'common.error'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="season-form">
      {overlapError && <p className="error-message">{overlapError}</p>}

      <SeasonFieldset
        value={fields}
        onChange={setFields}
        courseId={courseId}
        termEndError={termOrderError}
        disabled={saving}
      />

      <div className="season-form__actions">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" loading={saving} disabled={Boolean(termOrderError)}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
};

export default SeasonForm;
